import { database } from './firebase';
import { DB_PATHS } from './schema';
import { ref, get, query, orderByChild, limitToLast, update, runTransaction, onValue } from 'firebase/database';
import toastManager from '../utils/toastManager';

const toTimestamp = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
};

const normalizeSeverity = (value) => {
  const sev = String(value || '').toLowerCase();
  if (sev === 'success' || sev === 'warning' || sev === 'error') return sev;
  return 'info';
};

export const normalizeNotification = (id, raw = {}) => ({
  id,
  type: String(raw.type || 'system'),
  title: String(raw.title || 'Notification'),
  message: String(raw.message || ''),
  severity: normalizeSeverity(raw.severity),
  isRead: Boolean(raw.isRead),
  createdAt: toTimestamp(raw.createdAt),
  action: raw.action && typeof raw.action === 'object'
    ? {
      label: String(raw.action.label || 'Open'),
      route: String(raw.action.route || '/orders'),
    }
    : { label: 'Open', route: '/orders' }
});

export const subscribeUnreadNotifications = (uid, callback) => {
  if (!uid || typeof callback !== 'function') return () => {};
  const unreadPath = `${DB_PATHS.userNotificationsMeta(uid)}/unreadCount`;
  const unreadRef = ref(database, unreadPath);
  return onValue(unreadRef, (snapshot) => {
    const count = Number(snapshot.val() || 0);
    callback(Number.isFinite(count) ? count : 0);
  }, () => callback(0));
};

export const fetchRecentNotifications = async (uid, limit = 40) => {
  if (!uid) return [];
  const safeLimit = Number.isFinite(Number(limit)) ? Math.max(1, Math.min(100, Number(limit))) : 40;
  const notificationsRef = ref(database, DB_PATHS.userNotifications(uid));
  const notificationsQuery = query(notificationsRef, orderByChild('createdAt'), limitToLast(safeLimit));
  const snapshot = await get(notificationsQuery);
  if (!snapshot.exists()) return [];

  const list = [];
  snapshot.forEach((child) => {
    list.push(normalizeNotification(child.key, child.val() || {}));
  });

  return list.sort((a, b) => {
    if (b.createdAt !== a.createdAt) return b.createdAt - a.createdAt;
    return String(b.id).localeCompare(String(a.id));
  });
};

export const markNotificationRead = async (uid, notificationId) => {
  if (!uid || !notificationId) return false;
  const path = DB_PATHS.userNotification(uid, notificationId);
  const nodeRef = ref(database, path);
  const snapshot = await get(nodeRef);
  if (!snapshot.exists()) return false;

  const current = snapshot.val() || {};
  if (current.isRead) return true;

  await update(nodeRef, {
    isRead: true,
    updatedAt: Date.now()
  });

  const unreadRef = ref(database, `${DB_PATHS.userNotificationsMeta(uid)}/unreadCount`);
  await runTransaction(unreadRef, (prev) => {
    const currentCount = Number(prev) || 0;
    return currentCount > 0 ? currentCount - 1 : 0;
  });
  return true;
};

export const markAllNotificationsRead = async (uid, notifications = []) => {
  if (!uid || !Array.isArray(notifications) || notifications.length === 0) return false;
  const unreadIds = notifications.filter((n) => !n?.isRead).map((n) => n.id).filter(Boolean);
  if (unreadIds.length === 0) return true;

  const now = Date.now();
  const updates = {};
  unreadIds.forEach((id) => {
    updates[`${DB_PATHS.userNotification(uid, id)}/isRead`] = true;
    updates[`${DB_PATHS.userNotification(uid, id)}/updatedAt`] = now;
  });
  updates[`${DB_PATHS.userNotificationsMeta(uid)}/unreadCount`] = 0;

  await update(ref(database), updates);
  return true;
};

export const showNotificationToast = (notification) => {
  if (!notification) return;
  const severity = String(notification.severity || 'info').toLowerCase();
  const type = String(notification.type || '').toLowerCase();
  const title = String(notification.title || 'Notification').trim();
  const message = String(notification.message || '').trim();
  const content = message ? `${title}: ${message}` : title;
  const toastOptions = { duration: 3500 };

  if (type === 'telegram_not_linked') {
    toastManager.error(content, {
      duration: Infinity,
      id: 'telegram-not-linked-important'
    });
    return;
  }

  if (severity === 'success') {
    toastManager.success(content, toastOptions);
    return;
  }

  if (severity === 'error') {
    toastManager.error(content, toastOptions);
    return;
  }

  if (severity === 'warning') {
    toastManager.info(`Warning: ${content}`, toastOptions);
    return;
  }

  toastManager.info(content, toastOptions);
};

export const getNotificationRoute = (notification) => {
  const type = String(notification?.type || '').toLowerCase();
  const message = String(notification?.message || '').toLowerCase();
  if (type.includes('order')) return '/orders';
  if (type.includes('license_expiring') || type.includes('license_expired')) return '/my-scripts';
  if (type.includes('product_access')) return '/my-scripts';
  if (type.includes('script_request')) return '/orders';
  if (message.includes('expire')) return '/my-scripts';
  return String(notification?.action?.route || '/notifications');
};
